import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  Copy,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Collapse } from '@/components/Collapse';
import { ACCENT_COLORS, ColorPicker } from '@/components/ColorPicker';
import { Field } from '@/components/Field';
import { cn } from '@/lib/cn';
import { motionSafe, springDefault } from '@/lib/motion';
import { toast } from '@/lib/toast';
import { api, ApiError } from '@/lib/api';
import { useCurrentUser, useUpdatePreferences } from '@/features/auth/useAuth';
import { ProfileSettings } from '@/features/auth/ProfileSettings';
import { AiSettingsPanel } from '@/features/inbox/AiSettingsPanel';
import { ItflowSettingsPanel } from '@/features/billing/ItflowSettingsPanel';
import { EmailInboxPanel } from '@/features/inbox/EmailInboxPanel';
import { PomodoroSettingsPanel } from '@/features/time/PomodoroSettingsPanel';
import { useClients, useProjects } from '@/features/tasks/useTasks';
import type {
  CalendarAccount,
  CalendarProvider,
  CalendarSummary,
  CalendarUpdate,
} from './types';
import { calendarDisplayName } from './types';
import {
  useCalendarAccounts,
  useCalendarStatus,
  useConnectCalDav,
  useDisconnectAccount,
  useStartGoogle,
  useSyncAccount,
  useToggleCalendar,
} from './useCalendars';

const PROVIDER_LABEL: Record<CalendarProvider, string> = {
  chronos: 'Chronos',
  google: 'Google',
  icloud: 'iCloud',
  radicale: 'Radicale',
};

/* Sections: settings used to be one column of everything expanded at once.
   Splitting into panes means the page only shows the thing you came to change. */

const SECTIONS = [
  { id: 'profile', label: 'Profile' },
  { id: 'calendars', label: 'Calendars' },
  { id: 'plan', label: 'Plan display' },
  { id: 'focus', label: 'Focus & Pomodoro' },
  { id: 'ai', label: 'AI parsing' },
  { id: 'email', label: 'Email inbox' },
  { id: 'itflow', label: 'ITFlow billing' },
  { id: 'quick', label: 'Quick capture' },
  { id: 'feed', label: 'Calendar feed' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

function isSectionId(value: string | null): value is SectionId {
  return SECTIONS.some((s) => s.id === value);
}

function sectionFromUrl(): SectionId {
  const params = new URLSearchParams(window.location.search);
  const s = params.get('settings');
  return isSectionId(s) ? s : 'calendars';
}

function writeSettingsUrl(section: SectionId) {
  const params = new URLSearchParams(window.location.search);
  params.set('view', 'settings');
  params.set('settings', section);
  params.delete('calendar_error');
  const next = `/?${params.toString()}`;
  if (`${window.location.pathname}${window.location.search}` !== next) {
    window.history.replaceState(null, '', next);
  }
}

function minutesToTimeValue(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeValueToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

export function SettingsPage() {
  const [section, setSection] = useState<SectionId>(sectionFromUrl);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('calendar_error');
    if (err) toast.error(err);
    writeSettingsUrl(section);
  }, [section]);

  useEffect(() => {
    const onPop = () => setSection(sectionFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return (
    <div className="mx-auto w-full max-w-4xl px-5 py-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-title font-medium text-text">Settings</h1>
        <p className="text-small text-text-secondary">
          Your account, calendars, display hours, and the keys Chronos uses on
          your behalf.
        </p>
      </header>

      <div className="mt-6 flex flex-col gap-5 md:flex-row md:gap-8">
        {/* Rail on desktop, scrollable pill row on phones. Same control,
            same state -- the breakpoint only changes its axis. */}
        <nav
          aria-label="Settings sections"
          className="-mx-5 flex gap-1 overflow-x-auto px-5 [mask-image:linear-gradient(to_right,transparent,#000_0.75rem,#000_calc(100%-1.25rem),transparent)] md:mx-0 md:w-44 md:shrink-0 md:flex-col md:overflow-visible md:px-0 md:[mask-image:none]"
        >
          {SECTIONS.map((s) => {
            const active = s.id === section;
            return (
              <button
                key={s.id}
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => {
                  setSection(s.id);
                  writeSettingsUrl(s.id);
                }}
                className={cn(
                  'pressable min-h-touch shrink-0 rounded px-3 text-small',
                  'text-left transition-colors duration-press',
                  active
                    ? 'bg-surface-2 font-medium text-text'
                    : 'text-text-secondary hover:bg-surface-2 hover:text-text',
                )}
              >
                {s.label}
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1">
          {section === 'profile' && (
            <Pane
              title="Profile"
              description="Your name, sign-in email, password, and appearance."
            >
              <ProfileSettings />
            </Pane>
          )}
          {section === 'calendars' && <CalendarsSection />}
          {section === 'plan' && <PlanDayHoursSection />}
          {section === 'focus' && (
            <Pane
              title="Focus & Pomodoro"
              description="Work and break intervals, daily goal, and whether the Pomodoro clock runs alongside task time."
            >
              <PomodoroSettingsPanel />
            </Pane>
          )}
          {section === 'ai' && (
            <Pane
              title="AI parsing"
              description="Uses your own Anthropic key, for braindumps and email alike. Parsing only: scheduling is never done by the model."
            >
              <AiSettingsPanel />
            </Pane>
          )}
          {section === 'email' && (
            <Pane
              title="Email inbox"
              description="A capture mailbox checked every 5 minutes. Unread mail becomes task proposals in the Inbox for review."
            >
              <EmailInboxPanel />
            </Pane>
          )}
          {section === 'itflow' && (
            <Pane
              title="ITFlow billing"
              description="Connect an ITFlow instance so completed work can be handed to it as invoice lines. Client and project mapping lives on the Billing page."
            >
              <ItflowSettingsPanel />
            </Pane>
          )}
          {section === 'quick' && <QuickCaptureSection />}
          {section === 'feed' && <FeedSection />}
        </div>
      </div>
    </div>
  );
}

/** Shared pane frame: one heading, one line of context, then the controls. */
function Pane({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading font-medium text-text">{title}</h2>
        <p className="max-w-prose text-small text-text-secondary">{description}</p>
      </div>
      {children}
    </section>
  );
}

/* Calendars */

function CalendarsSection() {
  const status = useCalendarStatus();
  const accounts = useCalendarAccounts();

  const sorted = accounts.data
    ? [...accounts.data].sort((a, b) => {
        if (a.provider === 'chronos') return -1;
        if (b.provider === 'chronos') return 1;
        return a.id - b.id;
      })
    : [];

  return (
    <Pane
      title="Calendars"
      description="Pull busy time from Google, iCloud, or Radicale. One calendar is the write target for confirmed blocks."
    >
      <AddCalendar
        googleConfigured={status.data?.google_configured ?? false}
        googleRedirectUri={status.data?.google_redirect_uri}
        hasGoogle={accounts.data?.some((a) => a.provider === 'google') ?? false}
      />

      {accounts.isPending && (
        <div className="h-24 animate-pulse rounded-lg bg-surface-2" aria-hidden />
      )}

      {sorted.length > 0 && (
        <ul className="flex flex-col gap-3">
          {sorted.map((account) => (
            <AccountCard key={account.id} account={account} />
          ))}
        </ul>
      )}
    </Pane>
  );
}

/**
 * One connect affordance instead of three stacked cards.
 *
 * The old page kept a Google card, an iCloud card, and a Radicale card
 * permanently expanded above the calendars you actually use. Connecting is a
 * once-per-account act; it does not deserve permanent real estate.
 */
function AddCalendar({
  googleConfigured,
  googleRedirectUri,
  hasGoogle,
}: {
  googleConfigured: boolean;
  googleRedirectUri?: string;
  hasGoogle: boolean;
}) {
  const [open, setOpen] = useState<CalendarProvider | null>(null);
  const startGoogle = useStartGoogle();

  return (
    <div className="rounded-lg border border-border bg-surface-1">
      <div className="flex flex-wrap items-center gap-2 p-3">
        <span className="mr-auto inline-flex items-center gap-2 text-small text-text-secondary">
          <Plus size={15} strokeWidth={1.5} aria-hidden />
          Add a calendar
        </span>
        <Button
          variant={open === 'google' ? 'secondary' : 'ghost'}
          className="px-3"
          loading={startGoogle.isPending}
          onClick={() => {
            if (!googleConfigured) {
              setOpen((v) => (v === 'google' ? null : 'google'));
              return;
            }
            startGoogle.mutate(undefined, {
              onSuccess: ({ authorization_url }) =>
                window.location.assign(authorization_url),
              onError: (err) =>
                toast.error(
                  err instanceof ApiError ? err.message : 'Could not start Google OAuth',
                ),
            });
          }}
        >
          {hasGoogle ? 'Reconnect Google' : 'Google'}
        </Button>
        <Button
          variant={open === 'icloud' ? 'secondary' : 'ghost'}
          className="px-3"
          onClick={() => setOpen((v) => (v === 'icloud' ? null : 'icloud'))}
        >
          iCloud
        </Button>
        <Button
          variant={open === 'radicale' ? 'secondary' : 'ghost'}
          className="px-3"
          onClick={() => setOpen((v) => (v === 'radicale' ? null : 'radicale'))}
        >
          Radicale
        </Button>
      </div>

      <Collapse open={open !== null}>
        <div className="border-t border-border p-4">
          {open === 'google' && !googleConfigured && (
            <p className="text-small text-warning" role="status">
              Set <code className="font-mono">GOOGLE_CLIENT_ID</code> and{' '}
              <code className="font-mono">GOOGLE_CLIENT_SECRET</code> in{' '}
              <code className="font-mono">.env</code>
              {googleRedirectUri ? (
                <>
                  , then register redirect URI{' '}
                  <code className="break-all font-mono">{googleRedirectUri}</code>
                </>
              ) : null}
              .
            </p>
          )}
          {(open === 'icloud' || open === 'radicale') && (
            <CalDavConnectForm
              provider={open}
              onDone={() => setOpen(null)}
              onCancel={() => setOpen(null)}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
}

function CalDavConnectForm({
  provider,
  onDone,
  onCancel,
}: {
  provider: 'icloud' | 'radicale';
  onDone: () => void;
  onCancel: () => void;
}) {
  const connect = useConnectCalDav();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');

  const title = provider === 'icloud' ? 'iCloud' : 'Radicale';
  const hint =
    provider === 'icloud'
      ? 'Apple Account email and an app-specific password. Server defaults to caldav.icloud.com.'
      : 'Your Radicale base URL, username, and password.';

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate(
          {
            provider,
            username,
            password,
            url: url.trim() || undefined,
            label: label.trim() || undefined,
          },
          {
            onSuccess: () => {
              setUsername('');
              setPassword('');
              setUrl('');
              setLabel('');
              onDone();
              toast.show(`${title} connected`, { tone: 'success' });
            },
            onError: (err) => {
              toast.error(err instanceof ApiError ? err.message : 'Connect failed');
            },
          },
        );
      }}
    >
      <p className="text-small text-text-secondary">{hint}</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          autoComplete="username"
        />
        <Field
          label={provider === 'icloud' ? 'App-specific password' : 'Password'}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        {provider === 'radicale' && (
          <Field
            label="CalDAV URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            hint="Example: https://dav.example.com/user/"
          />
        )}
        <Field
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          hint="Optional display name"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={connect.isPending}>
          Connect {title}
        </Button>
      </div>
    </form>
  );
}

function AccountCard({ account }: { account: CalendarAccount }) {
  const disconnect = useDisconnectAccount();
  const sync = useSyncAccount();
  const isChronos = account.provider === 'chronos';
  const selectedCount = account.calendars.filter((c) => c.selected).length;

  return (
    <li className="overflow-hidden rounded-lg border border-border bg-surface-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
        <div className="mr-auto flex min-w-0 flex-col">
          <div className="flex min-w-0 items-baseline gap-2">
            <h3 className="truncate text-body font-medium text-text">{account.label}</h3>
            <span className="shrink-0 text-label uppercase text-text-tertiary">
              {PROVIDER_LABEL[account.provider]}
            </span>
          </div>
          <p className="text-small text-text-secondary">
            {isChronos ? (
              'Built-in: keeps blocks local and serves the ICS feed'
            ) : (
              <>
                {account.status === 'error' && (
                  <span className="text-danger">
                    {account.last_error || 'Sync error'}
                  </span>
                )}
                {account.status === 'needs_reauth' && (
                  <span className="text-warning">Needs re-authorization</span>
                )}
                {account.status === 'ok' &&
                  (account.last_synced_at
                    ? `Synced ${new Date(account.last_synced_at).toLocaleString()}`
                    : 'Not synced yet')}
                {selectedCount > 0 && (
                  <span className="text-text-tertiary">
                    {' · '}
                    {selectedCount} in use
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {!isChronos && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              className="px-2"
              aria-label={`Sync ${account.label} now`}
              loading={sync.isPending}
              onClick={() => {
                sync.mutate(account.id, {
                  onSuccess: () => toast.show('Sync queued', { tone: 'success' }),
                  onError: (err) =>
                    toast.error(err instanceof ApiError ? err.message : 'Sync failed'),
                });
              }}
            >
              <RefreshCw size={16} strokeWidth={1.5} aria-hidden />
            </Button>
            <Button
              variant="ghost"
              className="px-2"
              aria-label={`Disconnect ${account.label}`}
              loading={disconnect.isPending}
              onClick={() => {
                disconnect.mutate(account.id, {
                  onSuccess: () =>
                    toast.show('Calendar disconnected', { tone: 'success' }),
                  onError: (err) =>
                    toast.error(
                      err instanceof ApiError ? err.message : 'Disconnect failed',
                    ),
                });
              }}
            >
              <Trash2 size={16} strokeWidth={1.5} aria-hidden />
            </Button>
          </div>
        )}
      </div>

      {account.calendars.length > 0 && (
        <ul className="border-t border-border">
          {account.calendars.map((cal) => (
            <CalendarRow key={cal.id} calendar={cal} isChronos={isChronos} />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * One line per calendar.
 *
 * The checkbox is the only enable/disable control. Clicking the name (or the
 * chevron) opens colour, busy, and write-target options, including while the
 * calendar is off, so a leftover busy flag can be cleared.
 */
function CalendarRow({
  calendar: cal,
  isChronos = false,
}: {
  calendar: CalendarSummary;
  isChronos?: boolean;
}) {
  const toggle = useToggleCalendar();
  const [open, setOpen] = useState(false);
  const displayName = calendarDisplayName(cal);
  const [label, setLabel] = useState(displayName);
  const swatch = cal.color || ACCENT_COLORS[0]!.value;
  const detailId = `cal-detail-${cal.id}`;
  const syncedName = cal.synced_name ?? cal.name;
  const hasCustomName = Boolean(cal.custom_name?.trim());

  useEffect(() => {
    setLabel(calendarDisplayName(cal));
  }, [cal.custom_name, cal.name, cal.synced_name]);

  const dirty = label.trim() !== displayName;

  const saveLabel = () => {
    const trimmed = label.trim();
    const nextName = trimmed && trimmed !== syncedName ? trimmed : null;
    if ((nextName ?? syncedName) === displayName) {
      setLabel(displayName);
      return;
    }
    toggle.mutate(
      { calendarId: cal.id, custom_name: nextName },
      {
        onSuccess: () => toast.show('Display name saved', { tone: 'success' }),
        onError: (err) =>
          toast.error(
            err instanceof ApiError ? err.message : 'Could not update name',
          ),
      },
    );
  };

  return (
    <li className="border-b border-border last:border-b-0">
      <div className="flex min-h-touch items-center gap-3 px-4">
        <input
          id={`cal-${cal.id}`}
          type="checkbox"
          checked={cal.selected}
          disabled={isChronos}
          aria-label={`Use ${displayName} on Plan`}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            toggle.mutate(
              { calendarId: cal.id, selected: e.target.checked },
              {
                onError: (err) =>
                  toast.error(
                    err instanceof ApiError ? err.message : 'Could not update',
                  ),
              },
            );
          }}
          className="size-icon shrink-0 accent-[var(--accent)]"
        />

        <button
          type="button"
          aria-expanded={open}
          aria-controls={detailId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'pressable flex min-h-touch min-w-0 flex-1 items-center gap-2 py-2 text-left text-body',
            'rounded outline-none focus-visible:ring-2 focus-visible:ring-ring',
            cal.selected ? 'text-text' : 'text-text-secondary',
          )}
        >
          <span
            className="size-dot shrink-0 rounded-full"
            style={{ backgroundColor: swatch }}
            aria-hidden
          />
          <span className="truncate">{displayName}</span>
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {cal.is_write_target && (
              <span className="rounded-full bg-accent-quiet px-2 py-0.5 text-label uppercase text-accent">
                write
              </span>
            )}
            {cal.read_only && (
              <span className="text-label uppercase text-text-tertiary">read-only</span>
            )}
            <motion.span
              animate={{ rotate: open ? 180 : 0 }}
              transition={motionSafe(springDefault)}
              className="flex text-text-tertiary"
            >
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
            </motion.span>
          </span>
        </button>
      </div>

      <Collapse open={open}>
        <div id={detailId} className="flex flex-col gap-4 bg-surface-2 px-4 py-4 pl-11">
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              saveLabel();
            }}
          >
            <Field
              label="Display name"
              value={label}
              maxLength={255}
              onChange={(e) => setLabel(e.target.value)}
              hint={
                hasCustomName
                  ? `Imported as “${syncedName}”. This label is local to Chronos only.`
                  : 'Rename for your own reference; sync keeps the imported name.'
              }
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={!dirty} loading={toggle.isPending}>
                Save name
              </Button>
            </div>
          </form>

          <div className="flex flex-wrap gap-4 text-small text-text-secondary">
            {!isChronos && (
              <label className="inline-flex min-h-touch items-center gap-2">
                <input
                  type="checkbox"
                  checked={cal.is_busy_source}
                  onChange={(e) =>
                    toggle.mutate({
                      calendarId: cal.id,
                      is_busy_source: e.target.checked,
                    })
                  }
                  className="size-icon accent-[var(--accent)]"
                />
                Busy source
              </label>
            )}
            {!cal.read_only && (
              <label className="inline-flex min-h-touch items-center gap-2">
                <input
                  type="radio"
                  name="write-target"
                  checked={cal.is_write_target}
                  onChange={() =>
                    toggle.mutate({ calendarId: cal.id, is_write_target: true })
                  }
                  className="size-icon accent-[var(--accent)]"
                />
                Write target
              </label>
            )}
          </div>

          {!isChronos && <CalendarWorkDefaults calendar={cal} />}

          <ColorPicker
            label="Event colour"
            groupLabel={`${displayName} colour`}
            value={swatch}
            onChange={(color) => {
              toggle.mutate(
                { calendarId: cal.id, color },
                {
                  onError: (err) =>
                    toast.error(
                      err instanceof ApiError ? err.message : 'Could not update colour',
                    ),
                },
              );
            }}
          />
        </div>
      </Collapse>
    </li>
  );
}

/**
 * Client, project, and prep length for a whole calendar.
 *
 * This is the fallback, not an override: an event the user has already filed
 * by hand keeps what they chose, through the next sync and through any later
 * change made here. Setting a work calendar to a client once is what stops
 * every meeting from arriving unattributed.
 */
function CalendarWorkDefaults({ calendar: cal }: { calendar: CalendarSummary }) {
  const toggle = useToggleCalendar();
  const clients = useClients();
  const projects = useProjects(cal.default_client_id ?? undefined);
  const [prep, setPrep] = useState(String(cal.prep_minutes));

  useEffect(() => {
    setPrep(String(cal.prep_minutes));
  }, [cal.prep_minutes]);

  const save = (patch: Omit<CalendarUpdate, 'calendarId'>) =>
    toggle.mutate(
      { calendarId: cal.id, ...patch },
      {
        onError: (err) =>
          toast.error(
            err instanceof ApiError ? err.message : 'Could not update defaults',
          ),
      },
    );

  const prepValue = Number(prep);
  const prepValid = Number.isInteger(prepValue) && prepValue >= 1 && prepValue <= 480;
  const prepDirty = prepValid && prepValue !== cal.prep_minutes;

  return (
    <div className="flex flex-col gap-4 border-t border-border pt-4">
      <div className="flex flex-col gap-2">
        <label
          htmlFor={`cal-default-client-${cal.id}`}
          className="text-small font-medium text-text-secondary"
        >
          Default client
        </label>
        <select
          id={`cal-default-client-${cal.id}`}
          value={cal.default_client_id ?? ''}
          disabled={toggle.isPending}
          onChange={(e) =>
            save({
              default_client_id: e.target.value ? Number(e.target.value) : null,
            })
          }
          className="min-h-touch w-full rounded border border-border bg-surface-1 px-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
        >
          <option value="">No default</option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="text-small text-text-tertiary">
          Every event from this calendar files under this client. Change one
          event and that event keeps your choice, syncing will not undo it.
        </p>
      </div>

      {cal.default_client_id != null && (
        <>
          <div className="flex flex-col gap-2">
            <label
              htmlFor={`cal-default-project-${cal.id}`}
              className="text-small font-medium text-text-secondary"
            >
              Default project
            </label>
            <select
              id={`cal-default-project-${cal.id}`}
              value={cal.default_project_id ?? ''}
              disabled={toggle.isPending}
              onChange={(e) =>
                save({
                  default_project_id: e.target.value ? Number(e.target.value) : null,
                })
              }
              className="min-h-touch w-full rounded border border-border bg-surface-1 px-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
            >
              <option value="">No project</option>
              {(projects.data ?? []).map((pr) => (
                <option key={pr.id} value={pr.id}>
                  {pr.name}
                </option>
              ))}
            </select>
          </div>

          <label className="inline-flex min-h-touch items-center gap-2 text-small text-text-secondary">
            <input
              type="checkbox"
              checked={cal.default_billable}
              disabled={toggle.isPending}
              onChange={(e) => save({ default_billable: e.target.checked })}
              className="size-icon accent-[var(--accent)]"
            />
            Billable by default
          </label>
        </>
      )}

      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (prepDirty) save({ prep_minutes: prepValue });
        }}
      >
        <Field
          label="Prep time"
          type="number"
          min={1}
          max={480}
          step={5}
          value={prep}
          onChange={(e) => setPrep(e.target.value)}
          hint="Minutes an “Add prep time” task gets from this calendar’s events."
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={!prepDirty} loading={toggle.isPending}>
            Save prep time
          </Button>
        </div>
      </form>
    </div>
  );
}

/* Plan display */

const TIMEZONES = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'UTC',
] as const;

function PlanDayHoursSection() {
  const { data: user } = useCurrentUser();
  const update = useUpdatePreferences();
  const [start, setStart] = useState('07:00');
  const [end, setEnd] = useState('20:00');
  const [timezone, setTimezone] = useState('America/Chicago');

  useEffect(() => {
    if (!user) return;
    setStart(minutesToTimeValue(user.day_start_minute ?? 420));
    setEnd(minutesToTimeValue(user.day_end_minute ?? 1200));
    setTimezone(user.timezone || 'America/Chicago');
  }, [user]);

  const timezoneOptions = TIMEZONES.includes(timezone as (typeof TIMEZONES)[number])
    ? TIMEZONES
    : ([timezone, ...TIMEZONES] as string[]);

  return (
    <Pane
      title="Plan display"
      description="Timezone and visible hours for the Plan calendar. Synced events are stored in UTC and converted here."
    >
      <form
        className="flex flex-col gap-4 rounded-lg border border-border bg-surface-1 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          const day_start_minute = timeValueToMinutes(start);
          const day_end_minute = timeValueToMinutes(end);
          if (day_start_minute == null || day_end_minute == null) {
            toast.error('Enter valid times as HH:MM');
            return;
          }
          if (day_start_minute >= day_end_minute) {
            toast.error('Day start must be before day end');
            return;
          }
          update.mutate(
            { timezone, day_start_minute, day_end_minute },
            {
              onSuccess: () => toast.show('Plan display saved', { tone: 'success' }),
              onError: (err) =>
                toast.error(err instanceof ApiError ? err.message : 'Could not save'),
            },
          );
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2 sm:col-span-2">
            <label
              htmlFor="plan-timezone"
              className="text-small font-medium text-text-secondary"
            >
              Timezone
            </label>
            <select
              id="plan-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="min-h-touch rounded border border-border bg-surface-1 px-3 text-body text-text focus:border-accent-border"
            >
              {timezoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <Field
            label="Day starts"
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            required
          />
          <Field
            label="Day ends"
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            required
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={update.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Pane>
  );
}

/* Quick capture (Shortcuts / menu bar) */

function QuickCaptureSection() {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const token = useQuery({
    queryKey: ['auth', 'mcp-token'],
    queryFn: () => api<{ mcp_token: string }>('/api/auth/mcp-token'),
  });

  const rotate = useMutation({
    mutationFn: () =>
      api<{ mcp_token: string }>('/api/auth/mcp-token/rotate', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth', 'mcp-token'] });
      toast.show(
        'Quick capture token rotated. Update Shortcuts and the macOS menu bar (it will disconnect until you paste the new token)',
        { tone: 'success' },
      );
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not rotate token'),
  });

  const baseUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://your-chronos-domain';

  return (
    <Pane
      title="Quick capture"
      description="Bearer token for iOS Shortcuts, Siri, and the macOS menu bar. Captures land in Inbox for review; nothing is auto-committed."
    >
      {token.isPending && (
        <div className="h-24 animate-pulse rounded-lg bg-surface-2" aria-hidden />
      )}
      {token.data && (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-border bg-surface-1 p-4">
            <p className="mb-2 text-small font-medium text-text-secondary">API base URL</p>
            <code className="block break-all rounded bg-surface-sunken px-3 py-2 font-mono text-small text-text-secondary">
              {baseUrl}
            </code>
          </div>
          <div className="rounded-lg border border-border bg-surface-1 p-4">
            <p className="mb-2 text-small font-medium text-text-secondary">Bearer token</p>
            <code className="block break-all rounded bg-surface-sunken px-3 py-2 font-mono text-small text-text-secondary">
              {token.data.mcp_token}
            </code>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(token.data.mcp_token);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1600);
                  } catch {
                    toast.error('Could not copy');
                  }
                }}
              >
                {copied ? (
                  <Check size={16} strokeWidth={2} aria-hidden />
                ) : (
                  <Copy size={16} strokeWidth={1.5} aria-hidden />
                )}
                {copied ? 'Copied' : 'Copy token'}
              </Button>
              <Button
                variant="ghost"
                className="ml-auto"
                loading={rotate.isPending}
                onClick={() => rotate.mutate()}
              >
                <RefreshCw size={16} strokeWidth={1.5} aria-hidden />
                Rotate and revoke
              </Button>
            </div>
          </div>
          <p className="text-small text-text-tertiary">
            Use{' '}
            <code className="rounded bg-surface-sunken px-1 font-mono text-caption">
              Authorization: Bearer &lt;token&gt;
            </code>{' '}
            on{' '}
            <code className="rounded bg-surface-sunken px-1 font-mono text-caption">
              /api/quick/*
            </code>
            . See README → Quick capture for the iOS Shortcut recipe (Tier B + Tier C
            fallback).
          </p>
        </div>
      )}
    </Pane>
  );
}

/* Feed */

function FeedSection() {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const feed = useQuery({
    queryKey: ['feed', 'url'],
    queryFn: () => api<{ url: string; feed_token: string }>('/api/feed/url'),
  });

  const rotate = useMutation({
    mutationFn: () =>
      api<{ feed_token: string }>('/api/auth/feed-token/rotate', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feed', 'url'] });
      toast.show('Feed URL rotated. Update any subscriptions', { tone: 'success' });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not rotate feed'),
  });

  const url = feed.data?.url;

  return (
    <Pane
      title="Chronos calendar feed"
      description="Subscribe in Apple Calendar or Radicale and confirmed blocks appear as events. Google refreshes ICS subscriptions in hours, so prefer a write target there."
    >
      {feed.isPending && (
        <div className="h-24 animate-pulse rounded-lg bg-surface-2" aria-hidden />
      )}
      {url && (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-1 p-4">
          <code className="block break-all rounded bg-surface-sunken px-3 py-2 font-mono text-small text-text-secondary">
            {url}
          </code>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1600);
                } catch {
                  toast.error('Could not copy');
                }
              }}
            >
              {/* Confirmation lands on the button you pressed rather than a
                  toast in the corner you are not looking at. */}
              {copied ? (
                <Check size={16} strokeWidth={2} aria-hidden />
              ) : (
                <Copy size={16} strokeWidth={1.5} aria-hidden />
              )}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            <Button
              variant="ghost"
              className="ml-auto"
              loading={rotate.isPending}
              onClick={() => rotate.mutate()}
            >
              <X size={16} strokeWidth={1.5} aria-hidden />
              Rotate and revoke
            </Button>
          </div>
        </div>
      )}
    </Pane>
  );
}
